<i18n>
    [
        "browse__subnavViewAll--cta",
    ]
</i18n>

<template>
    <div class="c-curation-sub-nav">
        <Carousel
            :carouselItems="buttons"
            name="curation-sub-nav-carousel"
            :useVariableWidth="true"
            :shouldCenterContentForSinglePage="true"
            :carouselItemUniqueKey="carouselItemUniqueKey"
            :desktopSlideSize="4"
            :mediumSlideSize="4"
            :mobileSlideSize="2"
            :mobilePeekPercent="50"
            :mediumPeekPercent="50"
            :displaySliderFadient="true"
            :onlyShowPaginationOnHover="true"
            :noContainerMargin="true"
            :lightButtons="true"
            :catalogResponseId="null"
            :resetOnResize="true"
            :analyticsType="analyticsType"
            :defaultSlide="defaultIndex"
            :controlTabbing="true"
        >
            <template
                v-for="(button, index) in formattedButtons"
                :key="getCarouselSlotName(button, carouselItemUniqueKey, index)"
                #[getCarouselSlotName(button,carouselItemUniqueKey,index)]="{ isTabbable }"
            >
                <BasePill
                    :button="button"
                    :isActive="activeIndex === index"
                    :isTabbable="isTabbable"
                    @click="handleClick(button, index)"
                />
            </template>
        </Carousel>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useActions } from '~coreModules/core/js/composables/vuexHelpers';

import { VIEW_PRODUCT_LIST } from '~coreModules/core/js/global-event-constants';
import { CURATION_CLICK } from '~modules/core/js/global-event-constants';
import { PRODUCT_LIST_TYPES } from '~coreModules/analytics/js/analytics-constants';
import { CAROUSEL_ANALYTICS_TYPES } from '~modules/analytics/js/analytics-constants';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';

import { getCarouselSlotName } from '~coreModules/core/js/carousel-utils';
import { getFilterStringFromObject } from '~coreModules/catalog/js/catalog-utils';

import Carousel from '~coreModules/core/components/ui/Carousel.vue';
import BasePill from '~coreModules/core/components/ui/BasePill.vue';

const props = defineProps({
    contentfulCurationNavItems: {
        type: Array,
        default: () => [],
    },
    shouldShowViewAll: {
        type: Boolean,
        default: true,
    },
    updateCuration: {
        type: Function,
        default: null,
    },
    randomizeCurationNavItems: {
        type: Boolean,
        default: false,
    },
    contentTitle: {
        type: String,
        default: '',
    },
    contentModuleId: {
        type: String,
        default: '',
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const analyticsType = CAROUSEL_ANALYTICS_TYPES.CURATION_NAV_ITEM;

const carouselItemUniqueKey = 'slug';

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

const buttons = computed(() => [
    ...(props.shouldShowViewAll ? [{
        displayName: t('browse__subnavViewAll--cta'),
        slug: '',
    }] : []),
    ...props.contentfulCurationNavItems,
]);
const formattedButtons = computed(() => buttons.value.map(item => ({
    ...item,
    text: item?.displayName,
})));

// due to scoping issues we need to set the default and active index separately so the interaction happens immediate on
// click and not when query is updated
const defaultIndex = computed(() => {
    const index = buttons.value.findIndex(button => button.slug === route.query.curation);
    return index === -1 ? 0 : index;
});
const activeIndex = ref(defaultIndex.value);

function handleClick(button, index) {
    const newQuery = {
        ...route.query,
        curation: button.slug,
    };

    delete newQuery.sort;

    if (button.curationSortMethod) {
        newQuery.sort = button.curationSortMethod;
    }

    if (!index) {
        delete newQuery.curation;
    }

    trackGlobalEvent({
        type: CURATION_CLICK,
        data: {
            curation: button.slug,
            randomizeCurationNavItems: props.randomizeCurationNavItems,
            index,
            ...(!props.updateCuration && {
                filterString: getFilterStringFromObject(newQuery),
            }),
            contentTitle: props.contentTitle,
            contentModuleId: props.contentModuleId || button.title,
        },
    });

    if (props.updateCuration) {
        props.updateCuration(button);
        activeIndex.value = index;
        return;
    }

    // always remove pageNumber from query on pill click
    delete newQuery.pageNumber;

    router.push({
        query: newQuery,
    });

    activeIndex.value = index;
}

watch(() => props.hasEnteredViewport, (newValue, oldValue) => {
    if (!oldValue && newValue) {
        trackGlobalEvent({
            type: VIEW_PRODUCT_LIST,
            data: {
                type: PRODUCT_LIST_TYPES.CONTENT,
                products: [...props.contentfulCurationNavItems.map((item, index) => ({
                    productName: item.displayName,
                    productListId: item.slug,
                    productIndex: `${index}`,
                }))],
                randomizeCurationNavItems: props.randomizeCurationNavItems,
                contentTitle: props.contentTitle,
                contentModuleId: props.contentModuleId,
            },
        });
    }
}, { immediate: true });
</script>

<style lang="scss">
    .c-curation-sub-nav {
        padding: $nu-spacer-1 0;

        @include breakpoint(small) {
            padding: $nu-spacer-2 0;
        }
    }
</style>
